import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import axios from "../../axiosConfig";
import "../../styles/NotificationsBell.css";
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { IoMdNotificationsOutline } from 'react-icons/io'; // The bell icon

const WS_URL = process.env.REACT_APP_NOTIFICATIONS_WSS_URL || "http://localhost:8080/ws";

const NotificationsBell = ({ userId, webSocketUrl = WS_URL }) => {
    const { t } = useTranslation('common');
    const [unreadNotifications, setUnreadNotifications] = useState([]); // Unread notifications
    const [readNotifications, setReadNotifications] = useState([]); // Read notifications
    const [isOpen, setIsOpen] = useState(false);            // Dropdown open/close
    const [activeTab, setActiveTab] = useState('unread'); // 'unread' or 'read'
    const [isMobile, setIsMobile] = useState(false);
    const dropdownRef = useRef(null);

    // Detect mobile screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 480);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Log userId for debugging
    useEffect(() => {
        console.log("NotificationsBell: userId =", userId);
    }, [userId]);

    // Fetch unread notifications using axios
    useEffect(() => {
        if (!userId) return;
        console.log("Fetching unread notifications for user:", userId);
        axios
            .get(`/api/v1/notifications/unread/${userId}`)
            .then((response) => {
                console.log("Fetched unread notifications:", response.data);
                setUnreadNotifications(response.data);
            })
            .catch((error) => {
                console.error("Error fetching unread notifications:", error);
            });
    }, [userId]);

    // Fetch read notifications when read tab is opened
    const fetchReadNotifications = async () => {
        if (!userId) return;
        try {
            console.log("Fetching read notifications for user:", userId);
            const response = await axios.get(`/api/v1/notifications/read/${userId}`);
            console.log("Fetched read notifications:", response.data);
            setReadNotifications(response.data);
        } catch (error) {
            console.error("Error fetching read notifications:", error);
        }
    };

    // WebSocket subscription using SockJS
    useEffect(() => {
        const token = localStorage.getItem('jwt');
        const client = new Client({
            webSocketFactory: () => new SockJS(webSocketUrl),
            reconnectDelay: 5000,
            connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
        });

        client.onConnect = (frame) => {
            console.log('Connected: ', frame);
            client.subscribe(`/topic/notifications/${userId}`, (message) => {
                if (message.body) {
                    const notification = JSON.parse(message.body);
                    setUnreadNotifications((prev) => [notification, ...prev]);
                }
            });
        };

        client.onStompError = (frame) => {
            console.error('Broker reported error: ', frame.headers['message']);
            console.error('Additional details: ', frame.body);
        };

        client.activate();

        return () => {
            client.deactivate();
        };
    }, [userId]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const toggleDropdown = () => {
        setIsOpen((prev) => !prev);
    };

    // Handle tab switching
    const handleTabSwitch = (tab) => {
        setActiveTab(tab);
        if (tab === 'read') {
            fetchReadNotifications();
        }
    };

    // Mark all notifications as read using axios PATCH
    const markAllAsRead = async () => {
        if (!userId) return;
        try {
            await axios.patch(`/api/v1/notifications/mark-all/${userId}`);
            setUnreadNotifications([]);
        } catch (error) {
            console.error("Error marking all as read:", error);
        }
    };

    const hasUnread = unreadNotifications.length > 0;

    // Get tab labels based on screen size
    const getUnreadLabel = () => {
        if (isMobile) {
            return t('notifications.new', { count: unreadNotifications.length });
        }
        return t('notifications.unread', { count: unreadNotifications.length });
    };

    const getReadLabel = () => {
        return t('notifications.read');
    };

    return (
        <div className="notifications-bell" ref={dropdownRef}>
            <div className="bell-icon-wrapper" onClick={toggleDropdown}>
                <IoMdNotificationsOutline className="notification-icon" />
                {hasUnread && <span className="notification-dot"></span>}
            </div>
            {isOpen && (
                <div className="notifications-dropdown">
                    <div className="notifications-header">
                        <h4>{t('notifications.title')}</h4>
                    </div>

                    {/* Tab Navigation */}
                    <div className="notifications-tabs">
                        <button
                            className={`tab-button ${activeTab === 'unread' ? 'active' : ''}`}
                            onClick={() => handleTabSwitch('unread')}
                        >
                            {getUnreadLabel()}
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'read' ? 'active' : ''}`}
                            onClick={() => handleTabSwitch('read')}
                        >
                            {getReadLabel()}
                        </button>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'unread' && (
                        <>
                            {hasUnread && (
                                <div className="tab-actions">
                                    <button
                                        className="mark-all-as-read-button"
                                        onClick={markAllAsRead}
                                    >
                                        {t('notifications.markAllRead')}
                                    </button>
                                </div>
                            )}
                            <div className="notifications-list">
                                {unreadNotifications.length === 0 ? (
                                    <p>{t('notifications.noNew')}</p>
                                ) : (
                                    unreadNotifications.map((notif) => (
                                        <div key={notif.notificationId} className="notification-item">
                                            <p className="notification-message">{notif.message}</p>
                                            <span className="notification-time">
                                                {dayjs(notif.createdAt).format("YYYY-MM-DD HH:mm")}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === 'read' && (
                        <div className="notifications-list">
                            {readNotifications.length === 0 ? (
                                <p>{t('notifications.noRead')}</p>
                            ) : (
                                readNotifications.map((notif) => (
                                    <div key={notif.notificationId} className="notification-item read">
                                        <p className="notification-message">{notif.message}</p>
                                        <span className="notification-time">
                                            {dayjs(notif.createdAt).format("YYYY-MM-DD HH:mm")}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
